import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Modal,
  Tabs,
  Tab,
  TextField,
  Card,
} from "@mui/material";
import {
  Delete,
  Add,
  Edit,
  PlayCircleOutline,
  CloudUpload,
  ArrowBack,
  ArrowForward,
  Close,
  Image as ImageIcon,
  YouTube,
  Visibility,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";

interface Banner {
  contentUrl: string;
  imageLink?: string;
  contentType: "IMAGE" | "VIDEO";
  position: number;
}

interface BannerPickerProps {
  formikVal: Banner[];
  formikSetFieldValue: (banners: Banner[]) => void;
  pickerLabel: string;
  disabled?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const BannerPicker: React.FC<BannerPickerProps> = ({
  formikVal = [],
  formikSetFieldValue,
  pickerLabel,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [editingPosition, setEditingPosition] = useState<number>(1);
  const [activeTab, setActiveTab] = useState(0);
  const [tempImageLink, setTempImageLink] = useState("");
  const [tempYouTubeUrl, setTempYouTubeUrl] = useState("");

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => resolve(fileReader.result as string);
      fileReader.onerror = (error) => reject(error);
    });
  };

  const getYouTubeVideoId = (url: string): string | null => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const getYouTubeThumbnail = (videoId: string): string => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  const handleAddBanner = (position?: number) => {
    if (disabled || formikVal.length >= 4) return;

    const newPosition =
      position ||
      (formikVal.length > 0
        ? Math.max(...formikVal.map((b) => b.position)) + 1
        : 1);
    setEditingBanner(null);
    setEditingPosition(newPosition);
    setActiveTab(0);
    setTempImageLink("");
    setTempYouTubeUrl("");
    setModalOpen(true);
  };

  const handleEditBanner = (banner: Banner) => {
    if (disabled) return;

    setEditingBanner(banner);
    setEditingPosition(banner.position);
    setTempImageLink(banner.imageLink || "");
    setTempYouTubeUrl("");

    // Set active tab based on banner type
    if (
      banner.contentType === "VIDEO" &&
      banner.imageLink?.includes("youtube")
    ) {
      setActiveTab(1); // YouTube tab
      setTempYouTubeUrl(banner.imageLink);
    } else {
      setActiveTab(0); // Upload tab
    }

    setModalOpen(true);
  };

  const handleDeleteBanner = (position: number) => {
    const updatedBanners = formikVal
      .filter((banner) => banner.position !== position)
      .map((banner, index) => ({ ...banner, position: index + 1 }));
    formikSetFieldValue(updatedBanners);
  };

  const handleMoveBanner = (position: number, direction: "left" | "right") => {
    const bannerIndex = formikVal.findIndex((b) => b.position === position);
    if (bannerIndex === -1) return;

    const updatedBanners = [...formikVal];
    const targetIndex =
      direction === "left" ? bannerIndex - 1 : bannerIndex + 1;

    if (targetIndex >= 0 && targetIndex < updatedBanners.length) {
      // Swap positions
      [updatedBanners[bannerIndex], updatedBanners[targetIndex]] = [
        updatedBanners[targetIndex],
        updatedBanners[bannerIndex],
      ];

      // Update position numbers
      updatedBanners.forEach((banner, index) => {
        banner.position = index + 1;
      });

      formikSetFieldValue(updatedBanners);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const base64 = await convertToBase64(file);
      const isVideo = file.type.startsWith("video/");

      const newBanner: Banner = {
        contentUrl: base64,
        contentType: isVideo ? "VIDEO" : "IMAGE",
        position: editingPosition,
        imageLink: tempImageLink || undefined,
      };

      saveBanner(newBanner);
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const handleYouTubeSubmit = () => {
    if (!tempYouTubeUrl.trim()) return;

    const videoId = getYouTubeVideoId(tempYouTubeUrl);
    if (!videoId) {
      alert("Please enter a valid YouTube URL");
      return;
    }

    const thumbnailUrl = getYouTubeThumbnail(videoId);
    const newBanner: Banner = {
      contentUrl: thumbnailUrl,
      contentType: "VIDEO",
      position: editingPosition,
      imageLink: tempYouTubeUrl,
    };

    saveBanner(newBanner);
  };

  const saveBanner = (newBanner: Banner) => {
    const updatedBanners = [...formikVal];
    const existingIndex = updatedBanners.findIndex(
      (b) => b.position === editingPosition
    );

    if (existingIndex !== -1) {
      // Update existing banner
      updatedBanners[existingIndex] = newBanner;
    } else {
      // Add new banner
      updatedBanners.push(newBanner);
      // Sort by position
      updatedBanners.sort((a, b) => a.position - b.position);
    }

    formikSetFieldValue(updatedBanners);
    setModalOpen(false);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingBanner(null);
    setTempImageLink("");
    setTempYouTubeUrl("");
  };

  const handlePreviewBanner = () => {
    setCurrentPreviewIndex(0);
    setPreviewModalOpen(true);
  };

  const handlePreviousPreview = () => {
    setCurrentPreviewIndex((prev) =>
      prev === 0 ? formikVal.length - 1 : prev - 1
    );
  };

  const handleNextPreview = () => {
    setCurrentPreviewIndex((prev) =>
      prev === formikVal.length - 1 ? 0 : prev + 1
    );
  };

  const sortedBanners = [...formikVal].sort((a, b) => a.position - b.position);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          backgroundColor: "transparent",
          padding: "16px",
          transition: "border-color 0.2s ease",
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? "none" : "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "#6b7280",
            textAlign: "center",
            fontSize: "1.1rem",
            mb: 3,
          }}
        >
          Upload up to 4 image or video banners. An aspect ratio of 16:9 and a
          minimum size of 1280x720 are recommended for best quality.
        </Typography>
        {formikVal.length === 0 ? (
          /* Empty State */
          <Box sx={{ textAlign: "center" }}>
            <Box
              onClick={() => handleAddBanner()}
              sx={{
                cursor: "pointer",
                border: "2px dashed #d1d5db",
                borderRadius: "8px",
                padding: "40px 20px",
                mb: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#3b82f6",
                  backgroundColor: "#eff6ff",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: "64px",
                    height: "64px",
                    backgroundColor: "#f3f4f6",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ImageIcon sx={{ fontSize: "32px", color: "#9ca3af" }} />
                </Box>
                <Typography
                  variant="body1"
                  sx={{ color: "#4b5563", fontWeight: 500 }}
                >
                  Upload your first banner
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#6b7280", fontSize: "0.875rem" }}
                >
                  Images, videos, or YouTube links (16:9 recommended)
                </Typography>
              </Box>
            </Box>

            <Button
              onClick={() => handleAddBanner()}
              startIcon={<Add />}
              variant="contained"
              sx={{
                backgroundColor: "#2563eb",
                textTransform: "none",
                px: 3,
                py: 1.5,
                "&:hover": { backgroundColor: "#1d4ed8" },
              }}
            >
              Add Banner
            </Button>
          </Box>
        ) : (
          /* Banners Grid */
          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr 1fr",
                  sm: "1fr 1fr 1fr 1fr",
                },
                gap: 1.5,
                mb: 2,
                justifyContent: "center",
                maxWidth: "720px",
                width: "100%",
              }}
            >
              {formikVal.map((banner) => (
                <Box
                  key={banner.position}
                  sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                >
                  {/* Banner Preview */}
                  <Card
                    sx={{
                      position: "relative",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        paddingTop: "56.25%",
                        backgroundColor: "#f3f4f6",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={banner.contentUrl}
                        alt={
                          banner.contentType === "VIDEO"
                            ? "Video thumbnail"
                            : "Banner image"
                        }
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                      {banner.contentType === "VIDEO" && (
                        <PlayCircleOutline
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            color: "white",
                            fontSize: "1.5rem",
                          }}
                        />
                      )}
                    </Box>

                    {/* Position Indicator */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 4,
                        left: 4,
                        backgroundColor: "rgba(0,0,0,0.7)",
                        color: "white",
                        borderRadius: "10px",
                        padding: "2px 6px",
                        fontSize: "0.7rem",
                        fontWeight: 600,
                      }}
                    >
                      {banner.position}
                    </Box>
                  </Card>

                  {/* Always-Visible Controls */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Button
                        size="small"
                        onClick={() => handleEditBanner(banner)}
                        startIcon={<Edit />}
                        variant="outlined"
                        sx={{
                          flex: 1,
                          fontSize: "0.7rem",
                          py: 0.5,
                          textTransform: "none",
                          borderColor: "#d1d5db",
                          color: "#374151",
                          "&:hover": {
                            backgroundColor: "#f9fafb",
                            borderColor: "#9ca3af",
                          },
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleDeleteBanner(banner.position)}
                        startIcon={<Delete />}
                        variant="outlined"
                        sx={{
                          flex: 1,
                          fontSize: "0.7rem",
                          py: 0.5,
                          textTransform: "none",
                          borderColor: "#fca5a5",
                          color: "#dc2626",
                          "&:hover": {
                            backgroundColor: "#fef2f2",
                            borderColor: "#f87171",
                          },
                        }}
                      >
                        Delete
                      </Button>
                    </Box>

                    {/* Move buttons */}
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Button
                        size="small"
                        onClick={() =>
                          handleMoveBanner(banner.position, "left")
                        }
                        disabled={banner.position === 1}
                        startIcon={<ArrowBack />}
                        variant="outlined"
                        sx={{
                          flex: 1,
                          fontSize: "0.7rem",
                          py: 0.5,
                          textTransform: "none",
                          borderColor: "#d1d5db",
                          color: "#374151",
                          "&:hover": {
                            backgroundColor: "#f9fafb",
                            borderColor: "#9ca3af",
                          },
                          "&:disabled": {
                            borderColor: "#e5e7eb",
                            color: "#9ca3af",
                          },
                        }}
                      >
                        Left
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          handleMoveBanner(banner.position, "right")
                        }
                        disabled={banner.position === formikVal.length}
                        startIcon={<ArrowForward />}
                        variant="outlined"
                        sx={{
                          flex: 1,
                          fontSize: "0.7rem",
                          py: 0.5,
                          textTransform: "none",
                          borderColor: "#d1d5db",
                          color: "#374151",
                          "&:hover": {
                            backgroundColor: "#f9fafb",
                            borderColor: "#9ca3af",
                          },
                          "&:disabled": {
                            borderColor: "#e5e7eb",
                            color: "#9ca3af",
                          },
                        }}
                      >
                        Right
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))}

              {/* Add New Banner Card */}
              {formikVal.length < 4 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Card
                    onClick={() => handleAddBanner()}
                    sx={{
                      position: "relative",
                      paddingTop: "56.25%",
                      cursor: "pointer",
                      border: "2px dashed #d1d5db",
                      backgroundColor: "transparent",
                      "&:hover": {
                        borderColor: "#3b82f6",
                        backgroundColor: "#eff6ff",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Add sx={{ color: "#9ca3af", fontSize: "1.5rem" }} />
                    </Box>
                  </Card>
                  <Button
                    onClick={() => handleAddBanner()}
                    variant="contained"
                    sx={{
                      backgroundColor: "#2563eb",
                      textTransform: "none",
                      fontSize: "0.7rem",
                      py: 0.5,
                      "&:hover": { backgroundColor: "#1d4ed8" },
                    }}
                  >
                    Add Banner
                  </Button>
                </Box>
              )}
            </Box>

            {/* Preview Button */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button
                onClick={handlePreviewBanner}
                startIcon={<Visibility />}
                variant="contained"
                sx={{
                  backgroundColor: "#374151",
                  color: "white",
                  textTransform: "none",
                  px: 5,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderRadius: "12px",
                  boxShadow: "0 8px 25px rgba(55, 65, 81, 0.3)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#1f2937",
                    boxShadow: "0 12px 35px rgba(55, 65, 81, 0.4)",
                    transform: "translateY(-2px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Preview Banners
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Preview Modal */}
      <Modal open={previewModalOpen} onClose={() => setPreviewModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "95%",
            maxWidth: "900px",
            height: "85%",
            maxHeight: "700px",
            bgcolor: "white",
            borderRadius: "24px",
            boxShadow: "0 25px 60px rgba(0, 0, 0, 0.15)",
            p: 0,
            outline: "none",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Preview Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 4,
              backgroundColor: "white",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  mb: 0.5,
                }}
              >
                Banner Preview
              </Typography>
            </Box>
            <IconButton
              onClick={() => setPreviewModalOpen(false)}
              sx={{
                backgroundColor: "#f1f5f9",
                color: "#64748b",
                "&:hover": {
                  backgroundColor: "#e2e8f0",
                  color: "#475569",
                },
                width: 44,
                height: 44,
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Preview Content */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              position: "relative",
              backgroundColor: "white",
            }}
          >
            {sortedBanners.length > 0 && (
              <>
                {/* Banner Display */}
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "700px",
                    aspectRatio: "16 / 9",
                    backgroundColor: "white",
                    border: "3px solid #e2e8f0",
                    borderRadius: "16px",
                    overflow: "hidden",
                    position: "relative",
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {sortedBanners[currentPreviewIndex]?.contentType ===
                    "VIDEO" &&
                  sortedBanners[currentPreviewIndex]?.imageLink?.includes(
                    "youtube"
                  ) ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                        sortedBanners[currentPreviewIndex]?.imageLink || ""
                      )}?mute=1&autoplay=0&fs=0&loop=1&playsinline=1&color=white&rel=0`}
                      title="YouTube video player"
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                  ) : (
                    <img
                      src={sortedBanners[currentPreviewIndex]?.contentUrl}
                      alt={`Banner ${currentPreviewIndex + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  )}

                  {/* Video overlay for non-YouTube videos */}
                  {sortedBanners[currentPreviewIndex]?.contentType ===
                    "VIDEO" &&
                    !sortedBanners[currentPreviewIndex]?.imageLink?.includes(
                      "youtube"
                    ) && (
                      <PlayCircleOutline
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          color: "white",
                          fontSize: "5rem",
                          filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
                        }}
                      />
                    )}

                  {/* Navigation Arrows - positioned at left and right middle of banner */}
                  {sortedBanners.length > 1 && (
                    <>
                      <IconButton
                        onClick={handlePreviousPreview}
                        sx={{
                          position: "absolute",
                          left: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          backgroundColor: "rgba(255,255,255,0.9)",
                          color: "#475569",
                          backdropFilter: "blur(8px)",
                          border: "1px solid rgba(226, 232, 240, 0.8)",
                          width: 48,
                          height: 48,
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.95)",
                            transform: "translateY(-50%) scale(1.05)",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <ChevronLeft sx={{ fontSize: "1.8rem" }} />
                      </IconButton>
                      <IconButton
                        onClick={handleNextPreview}
                        sx={{
                          position: "absolute",
                          right: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          backgroundColor: "rgba(255,255,255,0.9)",
                          color: "#475569",
                          backdropFilter: "blur(8px)",
                          border: "1px solid rgba(226, 232, 240, 0.8)",
                          width: 48,
                          height: 48,
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.95)",
                            transform: "translateY(-50%) scale(1.05)",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <ChevronRight sx={{ fontSize: "1.8rem" }} />
                      </IconButton>
                    </>
                  )}

                  {/* Banner Indicators - positioned at bottom middle of banner */}
                  {sortedBanners.length > 1 && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        gap: 1,
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(12px)",
                        borderRadius: "24px",
                        padding: "12px 16px",
                        border: "1px solid rgba(226, 232, 240, 0.8)",
                        boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      {sortedBanners.map((_, index) => (
                        <Box
                          key={index}
                          onClick={() => setCurrentPreviewIndex(index)}
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            backgroundColor:
                              index === currentPreviewIndex
                                ? "#E1A731"
                                : "#cbd5e1",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor:
                                index === currentPreviewIndex
                                  ? "#c78f1e"
                                  : "#94a3b8",
                              transform: "scale(1.1)",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>

          {/* Preview Footer */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              backgroundColor: "white",
              borderTop: "1px solid #e2e8f0",
            }}
          >
            {/* Close Button - centered */}
            <Button
              onClick={() => setPreviewModalOpen(false)}
              variant="contained"
              sx={{
                backgroundColor: "#374151",
                color: "white",
                textTransform: "none",
                px: 6,
                py: 1.5,
                fontWeight: 600,
                borderRadius: "12px",
                boxShadow: "0 4px 15px rgba(55, 65, 81, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#1f2937",
                  boxShadow: "0 6px 20px rgba(55, 65, 81, 0.4)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Close Preview
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Upload/Edit Modal */}
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: "500px",
            bgcolor: "background.paper",
            borderRadius: "12px",
            boxShadow: 24,
            p: 0,
            outline: "none",
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 3,
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {editingBanner ? "Edit Banner" : "Add Banner"}
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <Close />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box sx={{ p: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{
                mb: 2,
                "& .MuiTabs-indicator": { backgroundColor: "#2563eb" },
              }}
            >
              <Tab
                icon={<CloudUpload />}
                label="Upload"
                sx={{ textTransform: "none", minHeight: 48 }}
              />
              <Tab
                icon={<YouTube />}
                label="YouTube"
                sx={{ textTransform: "none", minHeight: 48 }}
              />
            </Tabs>

            <TabPanel value={activeTab} index={0}>
              {editingBanner ? (
                // Editing existing banner - show current banner with change/remove buttons
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "400px",
                        width: "100%",
                        position: "relative",
                        paddingTop: "56.25%",
                        borderRadius: "12px",
                        border: "2px solid #e5e7eb",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                        overflow: "hidden",
                        backgroundColor: "#f3f4f6",
                      }}
                    >
                      <img
                        src={editingBanner.contentUrl}
                        alt="Current banner"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                      {editingBanner.contentType === "VIDEO" && (
                        <PlayCircleOutline
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            color: "white",
                            fontSize: "3rem",
                            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                          }}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                        maxWidth: "400px",
                      }}
                    >
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        variant="outlined"
                        startIcon={<Edit />}
                        sx={{
                          textTransform: "none",
                          borderColor: "#d1d5db",
                          color: "#374151",
                          px: 3,
                          "&:hover": {
                            backgroundColor: "#f9fafb",
                            borderColor: "#9ca3af",
                          },
                        }}
                      >
                        Change Image
                      </Button>
                    </Box>

                    <Box sx={{ width: "100%", maxWidth: "400px" }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}
                      >
                        <TextField
                          label="Banner Link (optional)"
                          value={tempImageLink}
                          onChange={(e) => setTempImageLink(e.target.value)}
                          fullWidth
                          variant="outlined"
                          placeholder="https://example.com"
                          size="small"
                        />
                        <Button
                          onClick={() => {
                            const updatedBanner: Banner = {
                              ...editingBanner,
                              imageLink: tempImageLink || undefined,
                            };
                            saveBanner(updatedBanner);
                          }}
                          variant="contained"
                          sx={{
                            backgroundColor: "#10b981",
                            textTransform: "none",
                            minWidth: "80px",
                            height: "40px",
                            "&:hover": { backgroundColor: "#059669" },
                          }}
                        >
                          Save
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </>
              ) : (
                // Adding new banner - show upload interface
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                  />
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        border: "2px dashed #d1d5db",
                        borderRadius: "8px",
                        padding: "40px 20px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "#2563eb",
                          backgroundColor: "#eff6ff",
                        },
                      }}
                    >
                      <CloudUpload
                        sx={{ fontSize: "3rem", color: "#9ca3af", mb: 2 }}
                      />
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Click to upload or drag and drop
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Image up to 5MB
                      </Typography>
                    </Box>

                    <TextField
                      label="Banner Link (optional)"
                      value={tempImageLink}
                      onChange={(e) => setTempImageLink(e.target.value)}
                      fullWidth
                      variant="outlined"
                      sx={{ mt: 2 }}
                      placeholder="https://example.com"
                    />
                  </Box>
                </>
              )}
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              {editingBanner && (
                <Box sx={{ mb: 2, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Current Video:
                  </Typography>
                  <Box
                    sx={{
                      maxWidth: "400px",
                      width: "100%",
                      position: "relative",
                      paddingTop: "56.25%",
                      borderRadius: "12px",
                      border: "2px solid #e5e7eb",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      overflow: "hidden",
                      backgroundColor: "#f3f4f6",
                    }}
                  >
                    <img
                      src={editingBanner.contentUrl}
                      alt="Current YouTube thumbnail"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                    <PlayCircleOutline
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "white",
                        fontSize: "3rem",
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                      }}
                    />
                  </Box>
                </Box>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="YouTube URL"
                  value={tempYouTubeUrl}
                  onChange={(e) => setTempYouTubeUrl(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <Button
                  onClick={handleYouTubeSubmit}
                  variant="contained"
                  disabled={!tempYouTubeUrl.trim()}
                  sx={{
                    backgroundColor: "#2563eb",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#1d4ed8" },
                  }}
                >
                  {editingBanner ? "Update YouTube Video" : "Add YouTube Video"}
                </Button>
              </Box>
            </TabPanel>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default BannerPicker;
